import React, { useEffect, useState } from 'react';
import { ConfigProvider, message, Modal, Form, Input, Segmented } from 'antd';
import { QueryClientProvider } from 'react-query';
import { useStore } from './store/store';
import { queryClient } from './queryClient';
import { hexToRgba } from './utils/theme';
import { canUseWebEditor } from './utils/browser';
import Loader from './Loader';
import DocumentsFolderView, { newTemplateDefaults } from './components/RightSide/DocumentsFolderView';
import WebTemplatesPanel from './components/RightSide/WebTemplatesPanel';
import NewTemplateModal from './components/RightSide/NewTemplateModal';
import DocumentTypesSelector from './components/common/DocumentTypesSelector';
import AdminOnlyFormItem from './components/common/AdminOnlyFormItem';
import { duplicateTemplate, updateTemplateInfos } from './requests/templates';
import { useModalOpener } from './hooks';
import { pillButtonComponentsTheme } from './utils/styles';
import './proposalApp.css';
import './costingEmbed.css';

// Vue « bibliothèque » standalone de l'éditeur : rend DocumentsFolderView en
// plein écran, sans le reste de l'éditeur. Destinée à être embarquée en iframe
// dans le costing (admin → Modèles) via EDITOR_URL?jwt=<jwt>&view=library.
// L'auth passe par le JWT de l'URL (cf. axios.instance). Sans oid/ofid,
// currentDocument.type vaut 'template' → getTemplates cible l'endpoint admin
// des modèles/sections (+ getFolders). Cliquer un document ne l'applique pas
// ici : on notifie le parent (costing) pour qu'il ouvre le logiciel éditeur.
//
// Ce point de montage est le seul consommateur de cette vue (aucune autre
// route n'atteint LibraryApp) : le style costing (classe costing-embed, cf.
// costingEmbed.css) s'applique donc sans condition, sans affecter la même
// DocumentsFolderView utilisée en standalone par TemplateModal.jsx.
const COSTING_ACCENT = '#2166AB'; // blue06 de la palette costing (theme.js)

const selector = (state) => ({
  getTemplates: state.getTemplates,
  fetchDatas: state.fetchDatas,
  createTemplateInStore: state.createTemplateInStore,
  postTemplate: state.postTemplate,
  deleteTemplate: state.deleteTemplate,
  updateTemplateInfosStore: state.updateTemplateInfosStore,
  duplicateTemplateStore: state.duplicateTemplateStore,
  hiddenTemplates: state.hiddenTemplates,
  hiddenSections: state.hiddenSections,
  getHiddenTemplates: state.getHiddenTemplates,
  restoreTemplate: state.restoreTemplate,
  changeCurrentTab: state.changeCurrentTab,
  user: state.user,
});

const postToParent = (type, payload = {}) => {
  try {
    window.parent.postMessage({ source: 'proposal-library', type, ...payload }, '*');
  } catch (e) {
    // parent inaccessible (ouvert hors iframe) : sans effet.
  }
};

const LibraryApp = () => {
  const {
    getTemplates,
    fetchDatas,
    createTemplateInStore,
    postTemplate,
    deleteTemplate,
    updateTemplateInfosStore,
    duplicateTemplateStore,
    hiddenTemplates,
    hiddenSections,
    getHiddenTemplates,
    restoreTemplate,
    changeCurrentTab,
    user,
  } = useStore(selector);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [newTemplateDefaultRecursive, setNewTemplateDefaultRecursive] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Vue affichée : 'folders' (documents) ou 'web' (templates de site web),
  // même sélecteur que TemplateModal.jsx, réservé à canUseWebEditor.
  const [viewMode, setViewMode] = useState('folders');
  const [isEditInfosOpen, setIsEditInfosOpen] = useModalOpener(false);
  const [modalSelection, setModalSelection] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState({});
  const [form] = Form.useForm();

  useEffect(() => {
    // fetchDatas charge le user (métiers) pour NewTemplateModal ; sans document
    // courant (pas d'oid/ofid/tid) il s'arrête tôt. getTemplates charge la
    // bibliothèque + les dossiers.
    fetchDatas();
    getTemplates().finally(() => setIsLoading(false));
  }, [fetchDatas, getTemplates]);

  // Couleur d'accent costing pour les composants antd qui la lisent en dehors
  // du ConfigProvider (LeftSide.less, VariableHandler.less...), même pattern
  // que Layout.jsx — jamais mis à jour ici sinon (LibraryApp ne monte pas Layout).
  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', COSTING_ACCENT);
    document.documentElement.style.setProperty('--primary-color-light', hexToRgba(COSTING_ACCENT, 0.3));
    // Boutons des modales/popovers antd, portés sur <body> (cf. costingEmbed.css).
    document.body.classList.add('costing-embed-popups');
  }, []);

  useEffect(() => {
    if (showHidden && user?.role === 'superadmin') {
      getHiddenTemplates();
    }
  }, [showHidden, user?.role, getHiddenTemplates]);

  // Création d'un modèle depuis le costing : même séquence que l'éditeur
  // (createTemplateInStore → postTemplate sur un canvas vierge), avec le dossier
  // choisi. On ouvre ensuite l'éditeur sur le nouveau modèle pour le composer.
  //
  // postTemplate (store/api.js) ne renvoie rien à l'appelant (ses effets de
  // bord — dont state.currentTemplate — se font en interne) : LeftSide.jsx
  // s'appuie sur ça, il ne lit jamais de valeur de retour non plus. Un
  // ancien `if (res?.id)` ici était donc toujours faux et affichait l'erreur
  // même quand la création réussissait côté serveur (200 OK) — l'id du
  // template créé se lit dans le store après coup, pas dans un retour.
  const handleCreateTemplate = async (payload) => {
    const key = 'library-create-template';
    message.open({ type: 'loading', content: 'Création du document en cours', duration: 0, key });
    try {
      createTemplateInStore(payload.templatable);
      await postTemplate({ ...payload, folderId: payload.folderId });
      setNewTemplateOpen(false);
      message.open({ type: 'success', content: 'Document créé.', duration: 2, key });
      const createdId = useStore.getState().currentTemplate?.id;
      if (createdId) postToParent('open-template', { tid: createdId });
      getTemplates();
    } catch (e) {
      message.open({ type: 'error', content: 'Une erreur est survenue lors de la création.', duration: 4, key });
    }
  };

  // Renommer / dupliquer : même formulaire que TemplateModal.jsx (édition du
  // nom + tags pour un renommage, nom du duplicata pour une duplication).
  const showEditInfosModal = (origin, template) => {
    setIsEditInfosOpen(true);
    setModalSelection(origin);
    setSelectedTemplate(template);
    if (origin === 'updateTemplateInfos') {
      form.setFieldsValue({ name: template.name, document_type_ids: template.document_type_ids, admin_only: template.admin_only });
    } else {
      form.setFieldsValue({ name: '' });
    }
  };

  const handleEditInfosOk = () => {
    const values = form.getFieldsValue();
    if (modalSelection === 'updateTemplateInfos') {
      updateTemplateInfos(selectedTemplate.id, {
        name: values.name,
        document_type_ids: values.document_type_ids || [],
        admin_only: values.admin_only,
      }).then((e) => {
        updateTemplateInfosStore(e);
      });
    } else if (modalSelection === 'duplicateTemplate') {
      duplicateTemplate(selectedTemplate.id, values.name).then((duplicated) => {
        if (!duplicated) return;
        duplicateTemplateStore(duplicated);
        // Ouvre la copie, comme un document créé ici.
        postToParent('open-template', { tid: duplicated.id });
      });
    }
    setIsEditInfosOpen(false);
  };

  const handleEditInfosCancel = () => {
    setIsEditInfosOpen(false);
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: COSTING_ACCENT,
          // Police body du costing (_fonts.scss, même ordre) — vérifié via
          // Playwright/Chromium headless (getComputedStyle réel) que c'est
          // bien elle qui rend le bouton "Créer une offre" (Bootstrap, pas
          // de l'antd) sur /offers, contrairement au tableau (vrai antd
          // Table) qui reste sur la pile système du ConfigProvider costing —
          // cf. le commentaire détaillé de costingEmbed.css. Couvre ici les
          // popups antd portées hors de .costing-embed (Modal, Popconfirm,
          // Tooltip...) ; costingEmbed.css fait le même override en CSS
          // (!important, .costing-embed *) pour tout le reste.
          fontFamily: "'Helvetica', 'Nunito', sans-serif",
        },
        components: { ...pillButtonComponentsTheme },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <div
          className='costing-embed'
          style={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            padding: 16,
            boxSizing: 'border-box',
          }}
        >
          {canUseWebEditor() && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 16 }}>
              <Segmented
                value={viewMode}
                onChange={setViewMode}
                options={[
                  { label: 'Documents', value: 'folders', title: '' },
                  { label: '🌐 Site web', value: 'web', title: '' },
                ]}
              />
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {isLoading && (
              <Loader
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                imageStyle={{ width: 150 }}
              />
            )}
            {!isLoading && viewMode === 'folders' && (
              <DocumentsFolderView
                onApply={(tid) => postToParent('open-template', { tid })}
                onNewTemplate={(docFilter) => {
                  // Même pré-remplissage que l'éditeur (TemplateModal) : la
                  // modale lit `currentTab` à son montage.
                  const { isSection, isRecursive } = newTemplateDefaults(docFilter);
                  if (isSection !== undefined) changeCurrentTab(isSection);
                  setNewTemplateDefaultRecursive(isRecursive);
                  setNewTemplateOpen(true);
                }}
                onRename={(record) => showEditInfosModal('updateTemplateInfos', record)}
                onDuplicate={(record) => showEditInfosModal('duplicateTemplate', record)}
                onDelete={(id) => deleteTemplate(id)}
                showHidden={showHidden}
                onShowHiddenChange={user?.role === 'superadmin' ? setShowHidden : undefined}
                hiddenTemplates={hiddenTemplates}
                hiddenSections={hiddenSections}
                onRestore={restoreTemplate}
                iconStyle='duotone'
              />
            )}
            {!isLoading && viewMode === 'web' && <WebTemplatesPanel iconStyle='duotone' />}
          </div>
        </div>
        {newTemplateOpen && (
          <NewTemplateModal
            type='template'
            open
            handleCancel={() => setNewTemplateOpen(false)}
            handleOk={handleCreateTemplate}
            defaultIsRecursive={newTemplateDefaultRecursive}
          />
        )}
        <Modal
          title={modalSelection === 'updateTemplateInfos' ? 'Edition du document' : 'Duplication du document'}
          open={isEditInfosOpen}
          onOk={handleEditInfosOk}
          onCancel={handleEditInfosCancel}
          cancelText='Annuler'
          width='50%'
          destroyOnClose
        >
          {modalSelection === 'updateTemplateInfos' ? (
            <Form form={form} layout='horizontal' labelCol={{ flex: '150px' }} labelAlign='left' labelWrap style={{ marginTop: 16 }}>
              <Form.Item label='Nouveau nom' name='name'>
                <Input key='renameInput' placeholder='Nouveau nom' size='middle' />
              </Form.Item>
              <Form.Item label='Tags' name='document_type_ids'>
                <DocumentTypesSelector />
              </Form.Item>
              <AdminOnlyFormItem />
            </Form>
          ) : modalSelection === 'duplicateTemplate' ? (
            <Form form={form} layout='horizontal' labelAlign='left' style={{ marginTop: 16 }}>
              <div style={{ marginBottom: '3%', display: 'flex' }}>
                Document dupliqué :
                <div style={{ fontWeight: 'bold', marginLeft: '1%' }}>{selectedTemplate.name}</div>
              </div>
              <Form.Item label='Nom du template dupliqué' name='name'>
                <Input key='renameInput' placeholder='Nouveau nom du modèle dupliqué' size='middle' />
              </Form.Item>
            </Form>
          ) : null}
        </Modal>
      </QueryClientProvider>
    </ConfigProvider>
  );
};

export default LibraryApp;
